import { LagTypeEnum } from 'redux-store/api/company';

export interface IPaymentMethod {
  id: number;
  companyId: number;
  ccExpireMonth: number;
  ccExpireYear: number;
  ccNum: string;
  type: string;
  createdAt: Date;
}

export type PaymentPlanRecurring = 'ONE_TIME' | 'MONTHLY' | 'ANNUAL';
export type PaymentPlanStatus = 'TRIALING' | 'ACTIVE' | 'INACTIVE';

export enum PromoCodeEnum {
  DISCOUNT = 'DISCOUNT',
  FIRST_PROJECT_FREE = 'FIRST_PROJECT_FREE',
  LIFETIME_DEAL = 'LIFETIME_DEAL',
}

export interface IPaymentPlanItem {
  id: number;
  name: string;
  recurring: PaymentPlanRecurring;
  externalProductId: number;
  amount: number;
  data: { maxProjectsAmount: number; lifetimeDeal?: boolean };
  couponCode: {
    code: string;
    discount: number;
  };
}

export interface IPaymentPlan {
  items: IPaymentPlanItem[];
}

export type IProductType = 'BILLING_PLAN' | 'BILLING_PLAN_TRIAL';

export interface ICurrentPaymentPlan {
  purchaseId: number;
  productId: number;
  name: string;
  amount: number;
  recurring: PaymentPlanRecurring;
  trialEndDate: string | null;
  activeUntil: string | null;
  activeProjectsAmount: number;
  maxProjectsAmount: number | null;
  status: PaymentPlanStatus;
  type: IProductType;
  needUpdatePaymentMethod: boolean;
  promoCodeType?: PromoCodeEnum;
  isPendingForCancel: boolean;
  nextBillingDate: string | null;
  isDelinquent: boolean;
  transaction: ITransaction;
}

export interface IBillingPlan {
  productId: number;
  type: IProductType;
  name: string;
  amount: number;
  recurring: PaymentPlanRecurring;
  trialEndDate: string;
  activeUntil: string;
  activeProjectsAmount: number;
  maxProjectsAmount: number;
  status: PaymentPlanStatus;
  purchaseId: number;
}

export interface IPaymentPlanBodyData {
  data: {
    productId: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    stripeKeys?: {
      stripeToken?: string;
      stripePm?: string;
    };
    creditCard?: {
      ccExpireMonth: number;
      ccExpireYear: string;
      ccNum: string;
      type: string;
    };
    couponCode?: string;
  };
}

export interface IPaymentPlanUpdateBody {
  data: {
    productId: number;
    couponCode: string;
  };
}

export interface ICancelSubscriptionBody {
  data: {
    purchaseId: number;
  };
}

export interface IUpdatePaymentMethodBody {
  data: {
    ccExpireMonth: number;
    ccExpireYear: string;
    ccNum: string;
    type: string;
    stripeKeys: {
      stripeToken: string;
    };
  };
}

export interface IPromoCodeBody {
  data: { code: string | null };
}

export interface IPaymentUpgrade {
  is_error: boolean;
  message: string;
  statusCode: number;
}

export interface IPaymentPlanUpgradeArgs {
  productId: number;
}
export interface IChangeTerms {
  html: string;
}

export interface IChangeTermsArgs {
  productId: number;
}

export interface IPaymentPlanUpgradeBody {
  data: {
    productId: number;
    couponCode?: string;
  };
}

type TBillingResponse<TData, TIsError extends boolean> = {
  is_error: TIsError;
  status_code: number;
  data: TData;
};

interface ITransaction {
  companyId: number;
  amount: number;
  status: ETransactionStatus;
  externalTransactionId: string;
  createdAt: Date;
  updatedAt: Date;
}

export enum ETransactionStatus {
  paid = 'PAID',
  failed = 'FAILED',
  pending = 'PENDING',
}

type TBillingErrorData = {
  status: string;
  purchase_id: number;
  client_secret: string;
  pi: string;
};

export type TCurrentPaymentPlanSuccess = TBillingResponse<ICurrentPaymentPlan, false>;
export type TCurrentPaymentPlanError = TBillingResponse<TBillingErrorData | undefined, true>;
export type TCurrentPaymentPlan = TCurrentPaymentPlanSuccess | TCurrentPaymentPlanError;

// Payment Plan Upgrade Responses
type TPaymentPlanUpgradeSuccessData = {
  message: string;
  status_code: number;
};

export type TPaymentPlanUpgradeSuccess = TBillingResponse<TPaymentPlanUpgradeSuccessData, false>;
export type TPaymentPlanUpgradeError = TBillingResponse<TBillingErrorData | undefined, true>;
export type TPaymentPlanUpgrade = TPaymentPlanUpgradeSuccess | TPaymentPlanUpgradeError;

// Confirm Payment Types
interface IConfirmationData {
  affiliate_commission_amount: string;
  affiliate_commission_percent: string;
  affiliate_email: string;
  affiliate_first_name: string;
  affiliate_last_name: string;
  amount: string;
  billing_address_1: string;
  billing_address_2: string;
  billing_city: string;
  billing_country: string;
  billing_state: string;
  billing_zip: string;
  buyer_email: string;
  buyer_first_name: string;
  buyer_ip: string;
  buyer_last_name: string;
  buyer_tax_name: string;
  buyer_tax_number: string;
  campaign_id: number;
  campaign_name: string;
  coupon_code: string;
  coupon_rate: string;
  coupon_type: string;
  currency: string;
  event: string;
  funnel_id?: null;
  funnel_name?: null;
  invoice_id: string;
  is_pending: number;
  is_rebill: number;
  mode: string;
  next_billing_date?: number;
  payment_processor: string;
  product_id: number;
  product_name: string;
  ref_affiliate_commission_amount: string;
  ref_affiliate_commission_percent: string;
  ref_affiliate_email: string;
  ref_affiliate_first_name: string;
  ref_affiliate_last_name: string;
  shipping_address_1: string;
  shipping_address_2: string;
  shipping_city: string;
  shipping_country: string;
  shipping_state: string;
  shipping_zip: string;
  tax_amount: string;
  tax_percent: string;
  tax_transaction_id: string;
  tracking_id: string;
  transaction_id: string;
  transaction_time: number;
  transaction_type: string;
  vendor_email: string;
  vendor_first_name: string;
  vendor_last_name: string;
  licenses: string;
  hash: string;
  verification_code: string;
}

export interface IConfirmPaymentBody {
  data: {
    invoiceId: string;
    stripePi: string;
  };
}

// Confirm Payment Responses
type TConfirmPaymentSuccess = {
  is_error: false;
  status_code: number;
  data: IConfirmationData;
  url: string;
  type: number;
  plan: number;
  next_page_url: string;
};

type TConfirmPaymentError = {
  is_error: true;
  status_code: number;
  message: string;
};

// export type TConfirmPaymentSuccess = TBillingResponse<TConfirmPaymentSuccessData, false>;

// export type TConfirmPaymentError = TBillingResponse<TConfirmPaymentErrorData, true>;

export type TConfirmPayment = TConfirmPaymentSuccess | TConfirmPaymentError;

export interface IChangeTerms {
  html: string;
}

export interface IChangeTermsArgs {
  productId: number;
}

// Company Billing Plan Websocket Events

enum TCompanyBillingPlanEvents {
  CREATED = 'created',
  UPDATED = 'updated',
}

enum BillingPlanStatus {
  active = 'ACTIVE',
  inactive = 'INACTIVE',
  trialing = 'TRIALING',
}

type TProductData = {
  maxProjectsAmount: number | null;
  lifetimeDeal?: boolean;
  firstProjectFree?: boolean;
  isEnterprisePlan?: boolean;
};

export interface ICompanyBillingPlan {
  id: number;
  companyId: number;
  productId: number;
  lifeTimeProductId: number;
  amount: number;
  trialEndDate: Date | null;
  activeUntil: Date | null;
  status: BillingPlanStatus;
  purchaseId: number | null;
  isPendingForCancel: boolean;
  data: TProductData | null;
  period: string;
  nextBillingDate: Date | null;
  invoiceId: string | null;

  productName: string | null;
}

interface ICompany {
  id: number;
  name: string;
  buildingStatisticId: number;
  phone: string | null;
  address?: string | null;
  secondAddress?: {};
  country?: string | null;
  state?: string | null;
  stateAbbr?: string | null;
  city?: string | null;
  zip?: string | null;
  lat?: number | null;
  lng?: number | null;
  logo?: string | null;
  workingDays: number[];
  utcOffset?: number;
  lagType?: LagTypeEnum;
  billingPlan: ICompanyBillingPlan;
}

export interface ICompanyBillingPlanData {
  companyProfile: ICompany;
}

export type TCompanyBillingPlanEvent = {
  event: TCompanyBillingPlanEvents;
  data: ICompanyBillingPlanData;
};

export interface IReactivateSubscriptionArgs {
  data: IStripeType;
}

export interface IStripeType {
  stripeToken: string;
  ccNum: string;
  ccExpireMonth: number;
  ccExpireYear: number;
}
